import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ThumbsUp, ThumbsDown, Check } from 'lucide-react';
import { feedbackService } from '../../api/feedbackService';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Portal,
  Box,
  Text,
  Textarea,
  Button,
  Flex,
  HStack,
} from '@chakra-ui/react';

type Props = {
  conversationRef: string | number;
  threadMessageId: string; // May be non-UUID in legacy/linear path
  threadId?: string;
  compact?: boolean;
  localOnly?: boolean; // when true, do not call backend; store in localStorage
  resolvedThreadMessageId?: string; // If provided, use this UUID for backend ops
  onResolve?: () => Promise<string | null>; // Called on-demand to resolve UUID
};

const POSITIVE_CHIPS = ['helpful', 'accurate', 'clear', 'fast'];
// Updated per spec: incorrect, unclear, off-topic, slow, unsafe
const NEGATIVE_CHIPS = ['incorrect', 'unclear', 'off_topic', 'slow', 'unsafe'];

function isUUIDLike(id: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id || '');
}

export const MessageFeedback: React.FC<Props> = ({ conversationRef, threadMessageId, threadId, compact, localOnly, resolvedThreadMessageId, onResolve }) => {
  const [rating, setRating] = useState<'up' | 'down' | null>(null);
  const [chips, setChips] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [lastSavedId, setLastSavedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showNote, setShowNote] = useState(false);
  const [openFor, setOpenFor] = useState<'up' | 'down' | null>(null);
  const [resolving, setResolving] = useState(false);
  const [effectiveThreadMessageId, setEffectiveThreadMessageId] = useState<string | null>(null);
  const upBtnRef = useRef<HTMLButtonElement | null>(null);
  const downBtnRef = useRef<HTMLButtonElement | null>(null);

  const chipSet = useMemo(() => (rating === 'down' ? NEGATIVE_CHIPS : POSITIVE_CHIPS), [rating]);
  const locked = useMemo(() => Boolean(lastSavedId), [lastSavedId]);

  // Establish effective thread message id
  useEffect(() => {
    if (localOnly) {
      setEffectiveThreadMessageId(threadMessageId || null);
      return;
    }
    if (resolvedThreadMessageId && isUUIDLike(resolvedThreadMessageId)) {
      setEffectiveThreadMessageId(resolvedThreadMessageId);
      return;
    }
    if (isUUIDLike(threadMessageId)) {
      setEffectiveThreadMessageId(threadMessageId);
      return;
    }
    setEffectiveThreadMessageId(null);
  }, [threadMessageId, resolvedThreadMessageId, localOnly]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (localOnly) {
        try {
          const key = `feedback:${conversationRef}:${threadMessageId}`;
          const raw = localStorage.getItem(key);
          if (raw) {
            const obj = JSON.parse(raw);
            if (!mounted) return;
            setRating(obj.rating ?? null);
            setChips(Array.isArray(obj.chips) ? obj.chips : []);
            setNote(obj.note || '');
            setLastSavedId('local');
          }
        } catch {}
        return;
      }
      // Hydrate existing feedback for this message from backend (only once we know the UUID)
      try {
        if (!effectiveThreadMessageId || !isUUIDLike(effectiveThreadMessageId)) return;
        const rows = await feedbackService.list(conversationRef, [effectiveThreadMessageId]);
        if (!mounted || !rows || !rows.length) return;
        const r = rows[0];
        setRating(r.rating);
        setChips(r.chips || []);
        setNote((r.note || '') as string);
        setLastSavedId(r.id);
      } catch {}
    })();
    return () => { mounted = false; };
  }, [conversationRef, threadMessageId, localOnly, effectiveThreadMessageId]);

  const post = async (next: { rating?: 'up' | 'down'; chips?: string[]; note?: string }) => {
    if (!rating && !next.rating) return; // require a polarity
    const resolvedId = localOnly ? threadMessageId : effectiveThreadMessageId;
    if (!localOnly && (!resolvedId || !isUUIDLike(resolvedId))) {
      setError('Message not ready yet; please try again in a moment.');
      return;
    }
    const body = {
      conversationRef,
      threadMessageId: resolvedId as string,
      threadId,
      rating: (next.rating || rating)!,
      chips: next.chips ?? chips,
      note: next.note ?? note,
    };
    setSaving(true);
    setError(null);
    // Enforce small UX delay (≤800ms) for submit feedback
    const minDelay = (ms: number) => new Promise((r) => setTimeout(r, ms));
    try {
      if (localOnly) {
        const key = `feedback:${conversationRef}:${threadMessageId}`;
        localStorage.setItem(key, JSON.stringify({ rating: body.rating, chips: body.chips, note: body.note }));
        setLastSavedId('local');
        await minDelay(300);
      } else {
        const req = feedbackService.upsert(body);
        await Promise.all([req, minDelay(300)]).then(async ([saved]) => {
          setLastSavedId((saved as any).id);
        });
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to save feedback');
    } finally {
      setSaving(false);
    }
  };

  const onThumb = async (val: 'up' | 'down') => {
    setError(null);
    // Once submitted, lock interactions (no undo, no reopening)
    if (locked) return;
    // Otherwise, set polarity and open popover anchored to clicked icon (no POST yet)
    setRating(val);
    setOpenFor(val);
    // Reset reasons when switching polarity for clarity
    setChips([]);

    // If we need to resolve a UUID, do it now (non-blocking UI, but we will disable Submit until resolved)
    if (!localOnly && !effectiveThreadMessageId && onResolve && !resolving) {
      try {
        setResolving(true);
        const id = await onResolve();
        if (id && isUUIDLike(id)) {
          setEffectiveThreadMessageId(id);
          // Hydrate from backend for the resolved id
          try {
            const rows = await feedbackService.list(conversationRef, [id]);
            if (rows && rows.length) {
              const r = rows[0];
              setRating(r.rating);
              setChips(r.chips || []);
              setNote((r.note || '') as string);
              setLastSavedId(r.id);
            }
          } catch {}
        }
      } catch (e: any) {
        setError(e?.message || 'Unable to resolve message. Please retry shortly.');
      } finally {
        setResolving(false);
      }
    }
  };

  const toggleChip = async (code: string) => {
    if (locked) return;
    const sel = new Set(chips);
    if (sel.has(code)) sel.delete(code); else sel.add(code);
    const arr = Array.from(sel).slice(0, 6);
    setChips(arr);
  };

  const onSubmit = async () => {
    await post({});
    // Close popover after submit
    setOpenFor(null);
  };

  const canSubmit = useMemo(() => {
    if (!rating) return false;
    if (rating === 'down') {
      return chips.length > 0 || note.trim().length > 0;
    }
    return true;
  }, [rating, chips, note]);

  const noteLimit = 400;
  const trimmedNote = note.slice(0, noteLimit);
  const placeholder = rating === 'down' ? 'What went wrong? (optional but helpful)' : 'Add a quick note (optional)';

  // Styles to keep selected thumb visibly finalized (blue outline)
  const blueOutline = '2px solid #3b82f6';
  const upStyle: React.CSSProperties = {
    fontSize: 12,
    color: rating === 'up' ? 'var(--color-primary)' : 'var(--color-text-muted)',
    outline: rating === 'up' && locked ? blueOutline : 'none',
    outlineOffset: '2px',
    borderRadius: '6px',
  };
  const downStyle: React.CSSProperties = {
    fontSize: 12,
    color: rating === 'down' ? 'var(--color-primary)' : 'var(--color-text-muted)',
    outline: rating === 'down' && locked ? blueOutline : 'none',
    outlineOffset: '2px',
    borderRadius: '6px',
  };

  return (
    <div style={{ display: 'inline-block', verticalAlign: 'middle', position: 'relative' }}>
      <HStack spacing={2} align="center">
        <Popover
          isOpen={openFor === 'up' && !locked}
          onClose={() => setOpenFor(null)}
          placement="auto-start"
          gutter={10}
          closeOnBlur
          closeOnEsc
          isLazy
        >
          <PopoverTrigger>
            <button
              ref={upBtnRef}
              className={`btn btn-link ${rating === 'up' ? 'active' : ''}`}
              aria-label="Thumbs up"
              title="Good"
              style={upStyle}
              onClick={() => onThumb('up')}
              disabled={saving || removing || locked}
            >
              <ThumbsUp size={16} />
            </button>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              width="360px"
              maxW="90vw"
              bg="gray.800"
              borderColor="gray.700"
              borderWidth="1px"
              borderRadius="12px"
              boxShadow="lg"
              zIndex={3000}
              _focus={{ outline: 'none' }}
            >
              <PopoverArrow bg="gray.800" borderColor="gray.700" />
              <PopoverBody p={4}>
                <Flex align="center" mb={3} gap={2}>
                  <ThumbsUp size={16} color="#60a5fa" />
                  <Text fontSize="sm" color="gray.200">Gave a thumbs up</Text>
                </Flex>
                <Box mb={3}>
                  <Flex wrap="wrap" gap={2}>
                    {POSITIVE_CHIPS.map(code => {
                      const selected = chips.includes(code);
                      return (
                        <Button
                          key={code}
                          size="sm"
                          variant={selected ? 'solid' : 'outline'}
                          onClick={() => toggleChip(code)}
                          leftIcon={selected ? <Check size={14} /> : undefined}
                          height="34px"
                          borderRadius="9999px"
                          colorScheme={selected ? 'blue' : undefined}
                          borderColor={selected ? 'blue.500' : 'gray.600'}
                          bg={selected ? 'blue.500' : 'transparent'}
                          isDisabled={locked}
                        >
                          {code.replace(/_/g, ' ')}
                        </Button>
                      );
                    })}
                  </Flex>
                </Box>
                <Box mb={3}>
                  {!showNote ? (
                    <Button variant="ghost" size="sm" onClick={() => !locked && setShowNote(true)} color="gray.300" px={0} isDisabled={locked}>
                      Add comment (optional)
                    </Button>
                  ) : (
                    <Box>
                      <Textarea
                        placeholder={placeholder}
                        value={trimmedNote}
                        onChange={(e) => !locked && setNote(e.target.value.slice(0, noteLimit))}
                        rows={2}
                        minH="56px"
                        maxH="120px"
                        resize="vertical"
                        fontSize="sm"
                        borderColor="gray.600"
                        _placeholder={{ color: 'gray.500' }}
                        isDisabled={locked}
                      />
                      {trimmedNote.length > 0 && (
                        <Flex justify="flex-end" mt={1}>
                          <Text fontSize="xs" color="gray.500">{trimmedNote.length}/{noteLimit}</Text>
                        </Flex>
                      )}
                    </Box>
                  )}
                </Box>
                {error && (
                  <Text fontSize="xs" color="red.400" mb={2}>{error}</Text>
                )}
                <Box>
                  <Button
                    width="100%"
                    colorScheme="blue"
                    onClick={onSubmit}
                    isLoading={saving || resolving}
                    loadingText={resolving ? 'Resolving...' : 'Submitting'}
                    isDisabled={locked || !canSubmit || (!localOnly && (!effectiveThreadMessageId || !isUUIDLike(effectiveThreadMessageId)))}
                  >
                    Submit
                  </Button>
                  <Button variant="ghost" size="sm" mt={2} onClick={() => setOpenFor(null)} color="gray.400">
                    Skip
                  </Button>
                </Box>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>

        <Popover
          isOpen={openFor === 'down' && !locked}
          onClose={() => setOpenFor(null)}
          placement="auto-start"
          gutter={10}
          closeOnBlur
          closeOnEsc
          isLazy
        >
          <PopoverTrigger>
            <button
              ref={downBtnRef}
              className={`btn btn-link ${rating === 'down' ? 'active' : ''}`}
              aria-label="Thumbs down"
              title="Bad"
              style={downStyle}
              onClick={() => onThumb('down')}
              disabled={saving || removing || locked}
            >
              <ThumbsDown size={16} />
            </button>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              width="360px"
              maxW="90vw"
              bg="gray.800"
              borderColor="gray.700"
              borderWidth="1px"
              borderRadius="12px"
              boxShadow="lg"
              zIndex={3000}
              _focus={{ outline: 'none' }}
            >
              <PopoverArrow bg="gray.800" borderColor="gray.700" />
              <PopoverBody p={4}>
                <Flex align="center" mb={3} gap={2}>
                  <ThumbsDown size={16} color="#f87171" />
                  <Text fontSize="sm" color="gray.200">Gave a thumbs down</Text>
                </Flex>
                <Box mb={3}>
                  <Flex wrap="wrap" gap={2}>
                    {NEGATIVE_CHIPS.map(code => {
                      const selected = chips.includes(code);
                      return (
                        <Button
                          key={code}
                          size="sm"
                          variant={selected ? 'solid' : 'outline'}
                          onClick={() => toggleChip(code)}
                          leftIcon={selected ? <Check size={14} /> : undefined}
                          height="34px"
                          borderRadius="9999px"
                          colorScheme={selected ? 'blue' : undefined}
                          borderColor={selected ? 'blue.500' : 'gray.600'}
                          bg={selected ? 'blue.500' : 'transparent'}
                          isDisabled={locked}
                        >
                          {code.replace(/_/g, ' ')}
                        </Button>
                      );
                    })}
                  </Flex>
                </Box>
                <Box mb={3}>
                  {!showNote ? (
                    <Button variant="ghost" size="sm" onClick={() => !locked && setShowNote(true)} color="gray.300" px={0} isDisabled={locked}>
                      Add comment (optional)
                    </Button>
                  ) : (
                    <Box>
                      <Textarea
                        placeholder={placeholder}
                        value={trimmedNote}
                        onChange={(e) => !locked && setNote(e.target.value.slice(0, noteLimit))}
                        rows={2}
                        minH="56px"
                        maxH="120px"
                        resize="vertical"
                        fontSize="sm"
                        borderColor="gray.600"
                        _placeholder={{ color: 'gray.500' }}
                        isDisabled={locked}
                      />
                      {(trimmedNote.length > 0) && (
                        <Flex justify="flex-end" mt={1}>
                          <Text fontSize="xs" color="gray.500">{trimmedNote.length}/{noteLimit}</Text>
                        </Flex>
                      )}
                    </Box>
                  )}
                </Box>
                {error && (
                  <Text fontSize="xs" color="red.400" mb={2}>{error}</Text>
                )}
                <Box>
                  <Button
                    width="100%"
                    colorScheme="blue"
                    onClick={onSubmit}
                    isLoading={saving || resolving}
                    loadingText={resolving ? 'Resolving...' : 'Submitting'}
                    isDisabled={locked || !canSubmit || (!localOnly && (!effectiveThreadMessageId || !isUUIDLike(effectiveThreadMessageId)))}
                  >
                    Submit
                  </Button>
                  <Button variant="ghost" size="sm" mt={2} onClick={() => setOpenFor(null)} color="gray.400">
                    Skip
                  </Button>
                </Box>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </HStack>
    </div>
  );
};

export default MessageFeedback;
